import { displayServerError } from "@services/NotificationService/NotifacitonService";
import axios from "axios";
import { debounce } from "lodash";
import { useEffect } from "react";
import { toast } from "react-toastify";

type BroadcastCallback = (filePublicId: string, collectionId?: string) => void;

interface UseDocumentEditBroadcastOptions {
  collectionId?: string;
  onDocumentUpdated: BroadcastCallback;
  debounceMs?: number;
  checkFileUpdated?: (
    filePublicId: string,
    onlyOfficeEditorKey?: string
  ) => Promise<boolean>;
  pollIntervalMs?: number;
  maxPollAttempts?: number;
}

export const useDocumentEditBroadcast = ({
  collectionId,
  onDocumentUpdated,
  debounceMs = 500,
  checkFileUpdated,
  pollIntervalMs = 10000,
  maxPollAttempts = 10,
}: UseDocumentEditBroadcastOptions) => {
  useEffect(() => {
    // Add this line to ensure test case not fail due to broadcast channel not being available
    // This is necessary because BroadcastChannel is not available in Node.js or during server-side rendering
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
      return;
    }

    const bc = new BroadcastChannel("docEditChannel");
    const pollUntilUpdated = async (
      filePublicId: string,
      collectionIdFromEvent?: string,
      onlyOfficeEditorKey?: string
    ) => {
      const fetchOnlyOfficeInfo = async () => {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/api/backend/onlyOffice/info`,
            {
              key: onlyOfficeEditorKey,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          return res.data?.users || [];
        } catch (error) {
          displayServerError();
          return [];
        }
      };

      if (collectionId && collectionId !== collectionIdFromEvent) return;

      const activeUsers = await fetchOnlyOfficeInfo();

      if (activeUsers.length >= 1) {
        toast.info(
          "Other users are currently editing this document. Your changes will be saved alongside theirs."
        );
        return; // No need to poll if multiple users are editing
      }

      const toastId = toast.info("Updating document, please wait...", {
        closeOnClick: true,
        closeButton: true,
        autoClose: 10000,
      });

      if (!checkFileUpdated) {
        toast.dismiss(toastId);
        toast.success("Document updated successfully.");
        onDocumentUpdated(filePublicId, collectionIdFromEvent);
        return;
      }

      for (let i = 0; i < maxPollAttempts; i++) {
        const isUpdated = await checkFileUpdated(filePublicId, onlyOfficeEditorKey);
        if (isUpdated) {
          toast.dismiss(toastId);
          toast.success("Document updated successfully.");
          onDocumentUpdated(filePublicId, collectionIdFromEvent);
          return;
        }
        await new Promise((res) => setTimeout(res, pollIntervalMs));
      }

      toast.warn("Document update not confirmed. Try refreshing manually.");
      toast.dismiss(toastId);
    };

    const debouncedPoll = debounce(
      (
        filePublicId: string,
        collectionIdFromEvent?: string,
        onlyOfficeEditorKey?: string
      ) => {
        pollUntilUpdated(filePublicId, collectionIdFromEvent, onlyOfficeEditorKey);
      },
      debounceMs
    );

    bc.onmessage = (event) => {
      const {
        type,
        filePublicId,
        collectionId: collectionIdFromEvent,
        onlyOfficeEditorKey,
      } = event.data || {};
      if (type === "doc-updated" && filePublicId) {
        debouncedPoll(filePublicId, collectionIdFromEvent, onlyOfficeEditorKey);
      }
    };

    return () => {
      bc.close();
      debouncedPoll.cancel();
    };
  }, [
    collectionId,
    onDocumentUpdated,
    debounceMs,
    checkFileUpdated,
    pollIntervalMs,
    maxPollAttempts,
  ]);
};
